/* eslint react/prop-types: [2, { ignore: ["setFocus", "onChange", "onNativeChange", "fieldName", "id", "name"] }] */
/* eslint-disable react/no-multi-comp */
import React from 'react';
import PropTypes from 'prop-types';
import FormElement, { removeNonStandardProps as removeFormElementProps } from '../FormElement';
import { resolve } from '@cvent/nucleus-dynamic-css';
import { injectTestId, resolveTestId } from '@cvent/nucleus-test-automation';

/**
Display browser default textbox

onChange = function(fieldName, newValue)
**/
export class Textbox extends React.PureComponent {
  static displayName = 'Textbox';
  static propTypes = {
    ...FormElement.propTypes,
    /** On Focus Callback */
    onFocus: PropTypes.func,
    /** The value of the input. */
    value: PropTypes.any.isRequired,
    /** The input type. */
    type: PropTypes.oneOf(['text', 'password', 'email', 'url', 'date', 'number']),
    /** Whether the input is disabled. */
    disabled: PropTypes.bool,
    /** Whether the input is readonly. */
    readOnly: PropTypes.bool,
    /** Placeholder text. */
    placeholder: PropTypes.string,
    /** The maxlength of the input. */
    maxLength: PropTypes.number,
    /** When to break down to 100% width */
    breakPoint: PropTypes.oneOf(['mediaSmall', 'mediaMedium', 'mediaLarge', 'mediaAll']),
    /** Available Style Keys. */
    style: PropTypes.shape({})
  };
  static defaultProps = {
    type: 'text',
    required: false,
    disabled: false,
    readOnly: false,
    placeholder: '',
    maxLength: null
  };
  constructor(props) {
    super(props);
    this.onBlur = this.onBlur.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onFocus = this.onFocus.bind(this);
  }

  componentDidMount() {
    if (this.props.setFocus) {
      this.focus();
    }
  }

  onChange(event) {
    const target = event.target ? event.target : window.event.srcElement;
    if (this.props.onNativeChange) {
      this.props.onNativeChange(event);
    } else {
      this.props.onChange(this.props.fieldName, target.value);
    }
  }

  onBlur(e) {
    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
  }

  onFocus(e) {
    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
  }

  select() {
    if (document && this.textbox) {
      this.textbox.select();
    }
  }

  focus() {
    if (document && this.textbox && document.activeElement !== this.textbox) {
      // Firing "focus" opens native date picker on iOS, but does nothing on Android
      this.textbox.focus();
      if (this.props.type === 'date') {
        // Firing "click" opens native date picker on Android, but does nothing on iOS
        this.textbox.click();
      }
    }
  }

  // NOTE: This is deprecated. Use the focus method above.
  // Set focus on <input type="date" />, if it isn't already focused.
  setFocusOnDateInput() {
    // DEPRECATED
    if (process.env.NODE_ENV !== 'production') {
      /* eslint-disable no-console */
      console.warn(`setFocusOnDateInput function has been deprecated and will
        be removed in the next major version. Please use "focus" instead.`);
      /* eslint-enable no-console */
    }
    if (this.props.type === 'date' && document && document.activeElement !== this.textbox) {
      // Firing "focus" opens native date picker on iOS, but does nothing on Android
      this.textbox.focus();
      // Firing "click" opens native date picker on Android, but does nothing on iOS
      this.textbox.click();
    }
  }

  render() {
    const { id, name, fieldName } = this.props;
    return (
      <input
        {...removeFormElementProps(this.props)}
        {...resolveTestId(this.props)}
        ref={(c) => { this.textbox = c; }}
        id={id || fieldName}
        name={name || fieldName}
        onBlur={this.onBlur}
        onChange={this.onChange}
        onFocus={this.onFocus} />
    );
  }
}

/**
FormElement wrapper around Textbox. This is the default export.
**/
class TextboxFormElement extends React.PureComponent {
  static displayName = 'TextboxFormElement';
  static propTypes = {
    /** The child content to display after the Textbox. */
    children: PropTypes.node,
    /** Prop that defines an additional available key for the style prop below. */
    size: PropTypes.string,
    /** When to break down to 100% width */
    breakPoint: PropTypes.oneOf(['mediaSmall', 'mediaMedium', 'mediaLarge', 'mediaAll']),
    /** Available Style Keys. */
    style: PropTypes.shape({
      /** Style object applied to the TextArea component. */
      textbox: PropTypes.object
    }),
    /**  Messages to display with the form element indicating there are errors.
     If not present it is assumed the input is valid **/
    errorMessages: PropTypes.objectOf(PropTypes.string),
    /** Determines if visual display of a valid input will display. I.E Green border and checkmark **/
    displayValid: PropTypes.bool,
    /** Validation state coming from the group component that consists of this component */
    validationState: PropTypes.oneOf(['valid', 'error', undefined])
  };

  static defaultProps = {
    size: '',
    errorMessages: {}
  };

  focus() {
    if (this.textbox) {
      this.textbox.focus();
    }
  }

  select() {
    if (this.textbox) {
      this.textbox.select();
    }
  }

  render() {
    const { children, size, breakPoint, displayValid, validationState, ...rest } = this.props;
    const { errorMessages } = rest;
    const errorsPresent = Object.keys(errorMessages).length > 0;
    const validState = displayValid ? 'valid' : undefined;
    const resolvedValidationState = validationState || (errorsPresent ? 'error' : validState);
    return (
      <FormElement {...rest}>
        <div {...resolve(this.props, 'textboxContainer')} >
          <Textbox
            {...rest}
            {...resolve(this.props, 'textbox', breakPoint, size, resolvedValidationState)}
            {...injectTestId('input')}
            ref={(c) => this.textbox = c}
          />
          {children}
        </div>
      </FormElement>
    );
  }
}

export default TextboxFormElement;
